import { makeAutoObservable } from 'mobx'
import {
  Condition,
  ConditionForm,
  RequestFormOptions,
  ServiceArea,
  WithIdName,
} from 'types/common'
import {
  displaySalaryNegotiableWithStockOption,
  getSalaryCondition,
  shortenItems,
} from 'utils/displays'
import { getMemberCountCondition } from 'utils/displays'
import { getDevCountCondition } from 'utils/displays'
import { getFlattenedData, getSelectedChildrenByParent } from 'utils/formatters'

export class PreferenceStore {
  conditions: Condition[] = []
  scoutRequest?: ConditionForm
  options?: RequestFormOptions

  constructor() {
    makeAutoObservable(this)
  }

  get importantConditions() {
    return this.conditions.filter(
      (cond) =>
        this.scoutRequest?.requiredConditions?.find(
          (i) => i.dataKey === cond.dataKey,
        )?.isRequired,
    )
  }

  get normalConditions() {
    return this.conditions.filter(
      (cond) =>
        !this.scoutRequest?.requiredConditions?.find(
          (i) => i.dataKey === cond.dataKey,
        )?.isRequired,
    )
  }

  setData(req: ConditionForm, opt: RequestFormOptions) {
    this.scoutRequest = req
    this.options = opt
  }

  setMultiSelectCond(dataKey: 'fundingRounds') {
    if (!this.scoutRequest || !this.options) return
    const selected = this.scoutRequest[dataKey]
    const options: WithIdName[] = this.options[dataKey]
    const arr = selected.reduce<string[]>((acc, item) => {
      const option = options.find((x) => x.id === item)
      if (option) acc.push(option.name)
      return acc
    }, [])
    const value = shortenItems(arr)
    this.conditions.push({ dataKey, value })
  }

  setLocationCond(dataKey: 'locations') {
    if (!this.scoutRequest || !this.options) return
    const selected = this.scoutRequest[dataKey]
    const selectedChildrenByParent = getFlattenedData(
      this.options.locations.filter((x) => !x.isRemote),
      selected,
    )
    const value = shortenItems(selectedChildrenByParent)
    this.conditions.push({ dataKey, value })
  }

  setServiceAreaCond(dataKey: 'serviceAreas') {
    if (!this.scoutRequest || !this.options) return
    const selected = this.scoutRequest.serviceAreas
    const options: ServiceArea[] = this.options.serviceAreas
    const selectedServiceAreas = options.filter((item) =>
      selected.includes(item.id),
    )
    const selectedChildrenByParent = getSelectedChildrenByParent(
      options,
      selectedServiceAreas,
    )
    const arr = selectedChildrenByParent.flatMap((parent) => {
      if (parent.selectedChildren.length === 0) return []
      if (parent.total === parent.selectedChildren.length)
        return [`${parent.name} 전체`]
      return parent.selectedChildren.map((child) => child.name)
    })
    const value = shortenItems(arr)
    this.conditions.push({ dataKey, value })
  }

  setMiscCond(
    dataKey:
      | 'shouldSupervisor'
      | 'shouldCto'
      | 'shouldStockOption'
      | 'remoteWorkPreference',
    value: string,
  ) {
    this.conditions.push({ dataKey, value })
  }

  setConditions() {
    if (!this.scoutRequest) return
    this.conditions = []
    const {
      locations,
      serviceAreas,
      memberCount,
      devCount,
      fundingRounds,
      salary,
      miscOptions,
    } = this.scoutRequest
    !!locations?.length && this.setLocationCond('locations')
    !!serviceAreas?.length && this.setServiceAreaCond('serviceAreas')
    !!fundingRounds?.length &&
      fundingRounds.length < 4 &&
      this.setMultiSelectCond('fundingRounds')
    if (memberCount) {
      const value = getMemberCountCondition(memberCount.min, memberCount.max)
      value && this.conditions.push({ dataKey: 'memberCount', value })
    }
    if (devCount) {
      const value = getDevCountCondition(devCount.min, devCount.max)
      value && this.conditions.push({ dataKey: 'devCount', value })
    }
    if (salary) {
      const value = getSalaryCondition(salary.min, salary.max)
      value &&
        value !== '연봉 무관' &&
        this.conditions.push({ dataKey: 'salary', value })
    }
    if (miscOptions) {
      miscOptions.shouldSupervisor &&
        this.setMiscCond('shouldSupervisor', '사수 있음')
      miscOptions.shouldCto && this.setMiscCond('shouldCto', 'CTO 있음')
      !!miscOptions.remoteWorkPreference &&
        this.setMiscCond(
          'remoteWorkPreference',
          miscOptions.remoteWorkPreference === 'FULL_REMOTE_NECESSARY'
            ? '원격 근무만 가능'
            : '부분 원격 근무 필요',
        )
      miscOptions.shouldStockOption &&
        this.setMiscCond(
          'shouldStockOption',
          displaySalaryNegotiableWithStockOption(
            miscOptions.isSalaryNegotiableWithStockOption,
          ),
        )
    }
  }
}
